import React, { ReactNode, useMemo } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import BotNoteView, { hasNoteContent } from "components/botsManagement/BotNoteView";
import BotsTopBar from "components/botsManagement/BotsTopBar";
import CustomScrollbars from "components/CustomScrollbars";
import useResponsive from "hooks/useResponsive";
import {
  BotNoteScope,
  useBotNoteEditor,
} from "components/botsManagement/EditableBotNote";
import {
  BotNote,
  BotNotesCatalogueRow,
  useGetBotNotesQuery,
} from "features/adminUsers/adminUsers";

/**
 * Every note on one screen (CAL-6162). The jobs panel edits each one where it
 * shows it, too.
 *
 * The catalogue is code, so this lists what the registry answers with and
 * lets an editor write beside it - never add to it. A note whose key the
 * registry has stopped answering for is listed at the end: it is kept rather
 * than deleted, because a bot renamed by mistake would take its note along.
 * Everything comes from the notes endpoint, so the screen needs no other
 * Automations request.
 */
export default function BotNotesPanel({
  switcher,
  onBack,
  canEdit,
}: {
  /** The client switcher, first crumb of the header. */
  switcher?: ReactNode;
  onBack?: () => void;
  canEdit: boolean;
}) {
  const { isCardMode } = useResponsive();
  const notes = useGetBotNotesQuery();
  const editor = useBotNoteEditor();

  const noteOf = useMemo(() => {
    const out: Record<string, BotNote> = {};
    for (const row of notes.data?.notes ?? []) out[`${row.scope}:${row.key}`] = row;
    return out;
  }, [notes.data]);

  const byVendor: Array<[string, BotNotesCatalogueRow[]]> = useMemo(() => {
    const out: Record<string, BotNotesCatalogueRow[]> = {};
    for (const row of notes.data?.catalogue ?? []) {
      (out[row.vendor] = out[row.vendor] ?? []).push(row);
    }
    return Object.keys(out)
      .sort((a, b) => a.localeCompare(b))
      .map((vendor) => [vendor, out[vendor]]);
  }, [notes.data]);

  const editButton = (scope: BotNoteScope, key: string, title: string, note: BotNote | null) => (
    <Button size="small" disabled={!canEdit} onClick={() => editor.open({ scope, key, title, note })}>
      {hasNoteContent(note) ? "Edit" : "Add note"}
    </Button>
  );

  const body = (
    <Box sx={{ px: { xs: 2, md: 3 }, py: 2 }}>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
        What a manager needs before running a bot: how many tasks at once, how
        fast it goes, what to check first, where its page is. The bots
        themselves come from the code - a note cannot add one or hide one.
      </Typography>
      {notes.isLoading ? <LinearProgress sx={{ mb: 1 }} /> : null}

      {byVendor.map(([vendor, bots]) => {
        const vendorNote = noteOf[`vendor:${vendor}`] ?? null;
        return (
          <Box key={vendor} sx={{ mb: 2, border: 1, borderColor: "divider", borderRadius: 1, p: 1.25 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
              <Typography fontWeight={700}>{vendor}</Typography>
              <Chip size="small" variant="outlined" label={`${bots.length} bot(s)`} sx={{ height: 20 }} />
              <Box sx={{ flex: 1 }} />
              {editButton("vendor", vendor, vendor, vendorNote)}
            </Stack>
            <BotNoteView note={vendorNote} dense />

            <Divider sx={{ my: 1 }} />
            {bots.map((bot) => {
              const botNote = noteOf[`bot:${bot.bot_id}`] ?? null;
              const name = `${bot.job_kind}.${bot.bot_slug}`;
              return (
                <Box key={bot.bot_id} sx={{ py: 0.5 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body2" sx={{ fontFamily: "monospace", minWidth: 0, overflowWrap: "anywhere" }}>
                      {name}
                    </Typography>
                    {!bot.is_active ? (
                      <Chip size="small" label="disabled" sx={{ height: 18, fontSize: "0.65rem" }} />
                    ) : null}
                    <Box sx={{ flex: 1 }} />
                    {editButton("bot", bot.bot_id, bot.bot_id, botNote)}
                  </Stack>
                  <BotNoteView note={botNote} dense />
                </Box>
              );
            })}
          </Box>
        );
      })}

      {notes.data?.orphans?.length ? (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" fontWeight={700}>
            Notes whose bot or vendor this build does not know
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Kept, and shown nowhere else. Repoint them, or delete them.
          </Typography>
          {notes.data.orphans.map((row) => (
            <Box key={`${row.scope}:${row.key}`} sx={{ py: 0.5 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Chip size="small" label={row.scope} sx={{ height: 20 }} />
                <Typography variant="body2" sx={{ fontFamily: "monospace", minWidth: 0, overflowWrap: "anywhere" }}>
                  {row.key}
                </Typography>
                <Box sx={{ flex: 1 }} />
                {editButton(row.scope as BotNoteScope, row.key, row.key, row.note)}
              </Stack>
              <BotNoteView note={row.note} dense />
            </Box>
          ))}
        </Box>
      ) : null}

      {!notes.isLoading && !byVendor.length ? (
        <Alert severity={notes.isError ? "error" : "info"} sx={{ mt: 2 }}>
          {notes.isError ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <span>The notes could not be loaded.</span>
              <Button size="small" onClick={() => void notes.refetch()}>
                Try again
              </Button>
            </Stack>
          ) : (
            "This build registers no bots, so there is nothing to note."
          )}
        </Alert>
      ) : null}
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", ...(isCardMode ? {} : { flex: 1, minHeight: 0 }) }}>
      <BotsTopBar
        lead={switcher}
        crumbs={onBack ? [{ label: "Major jobs", onClick: onBack }] : []}
        title="Notes for DPM"
        stacked={isCardMode}
      />
      {isCardMode ? body : <CustomScrollbars sx={{ flex: 1, minHeight: 0 }}>{body}</CustomScrollbars>}
      {editor.dialog}
    </Box>
  );
}
